import React, { useRef, useEffect } from 'react';
import {
    McDialog,
    McInput,
    McTextarea,
    McButton,
    McLoadingIndicator,
    McIcon,
    McCheckbox
} from '@maersk-global/mds-react-wrapper/components-core';
import { ConfirmationDialog } from '../../Common/ConfirmationDialog';

interface CreateVersionDialogProps {
    open: boolean;
    onClose: () => void;
    selectedQuery: any;
    schedules: any[];
    dialogError: string | null;
    loadingVersionNumber: boolean;
    versionForm: {
        versionNumber: string;
        sqlContent: string;
        changeDescription: string;
        createdBy: string;
        queryId: string;
        makeCurrent: boolean;
    };
    setVersionForm: React.Dispatch<React.SetStateAction<{
        versionNumber: string;
        sqlContent: string;
        changeDescription: string;
        createdBy: string;
        queryId: string;
        makeCurrent: boolean;
    }>>;
    onSubmit: () => void;
    isPending: boolean;
}

export const CreateVersionDialog: React.FC<CreateVersionDialogProps> = ({
    open,
    onClose,
    selectedQuery,
    schedules,
    dialogError,
    loadingVersionNumber,
    versionForm,
    setVersionForm,
    onSubmit,
    isPending
}) => {
    const latestOnClose = useRef(onClose);
    const latestOnSubmit = useRef(onSubmit);
    const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);

    useEffect(() => { latestOnClose.current = onClose; }, [onClose]);
    useEffect(() => { latestOnSubmit.current = onSubmit; }, [onSubmit]);

    return (
        <>
            <McDialog
                open={open}
                heading={`Create New Version for ${selectedQuery?.name}`}
                closed={onClose}
                width="1000px"
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                    {dialogError && (
                        <div style={{ backgroundColor: '#fdecea', color: '#d32f2f', padding: '12px', borderRadius: '4px', border: '1px solid #f5c6cb', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <McIcon icon="times-circle" />
                            <span className="mds-text--small-regular">{dialogError}</span>
                        </div>
                    )}
                    <div>
                        <McInput
                            label="Version Number"
                            value={versionForm.versionNumber}
                            input={(e: any) => setVersionForm(prev => ({ ...prev, versionNumber: e.target.value }))}
                            required
                            disabled={loadingVersionNumber}
                        >
                            {loadingVersionNumber && <McLoadingIndicator slot="suffix" />}
                        </McInput>
                        <div className="mds-text--small-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', marginTop: '4px' }}>
                            {loadingVersionNumber
                                ? "Generating next version number..."
                                : "Auto-generated based on existing versions. You can modify if needed."}
                        </div>
                    </div>

                    {/* Warning for active schedules */}
                    {selectedQuery && schedules?.some((s: any) => s.queryId === selectedQuery.queryId && s.enabled) && (
                        <div style={{ backgroundColor: '#fff4e5', color: '#663c00', padding: '12px', borderRadius: '4px', border: '1px solid #ffe2b7', fontSize: '0.875rem' }}>
                            <strong>Warning:</strong> This query has active schedules. Creating a new version will NOT automatically update the schedules. They will continue running the pinned version. You must update them manually.
                        </div>
                    )}

                    <div>
                        <McTextarea
                            label="SQL Content"
                            value={versionForm.sqlContent}
                            input={(e: any) => setVersionForm(prev => ({ ...prev, sqlContent: e.target.value }))}
                            rows={8}
                            required
                            style={{ fontFamily: '"JetBrains Mono", "Monaco", "Menlo", monospace' }}
                        />
                        <div className="mds-text--small-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', marginTop: '4px' }}>
                            Enter the SQL query for this version
                        </div>
                    </div>

                    <div>
                        <McTextarea
                            label="Change Description"
                            value={versionForm.changeDescription}
                            input={(e: any) => setVersionForm(prev => ({ ...prev, changeDescription: e.target.value }))}
                            rows={3}
                        />
                        <div className="mds-text--small-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', marginTop: '4px' }}>
                            Describe what changed in this version
                        </div>
                    </div>

                    <div style={{ padding: '8px 0' }}>
                        <McCheckbox
                            label="Make this the current version"
                            checked={versionForm.makeCurrent}
                            change={(e: any) => setVersionForm(prev => ({ ...prev, makeCurrent: e.detail.checked }))}
                        />
                        <div className="mds-text--small-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', marginTop: '4px', marginLeft: '32px' }}>
                            If checked, this version will immediately become the active version for this query.
                        </div>
                    </div>

                    <div>
                        <McInput
                            label="Created By"
                            value={versionForm.createdBy}
                            disabled
                        />
                        <div className="mds-text--small-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', marginTop: '4px' }}>
                            Auto-populated from your user account
                        </div>
                    </div>
                </div>
                <McButton
                    slot="secondaryAction"
                    appearance="primary"
                    variant="outlined"
                    style={{ width: '160px' }}
                    onClick={() => latestOnClose.current()}
                >
                    Cancel
                </McButton>
                <McButton
                    slot="primaryAction"
                    appearance="primary"
                    onClick={() => {
                        if (!isPending) setShowConfirmDialog(true);
                    }}
                    variant="filled"
                    disabled={isPending}
                    style={{ width: '160px' }}
                >
                    {isPending ? (
                        <McLoadingIndicator slot="prefix" />
                    ) : (
                        <McIcon icon="plus" slot="prefix" />
                    )}
                    {isPending ? 'Creating...' : 'Create Version'}
                </McButton>
            </McDialog>

            <ConfirmationDialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => {
                    setShowConfirmDialog(false);
                    latestOnSubmit.current();
                }}
                title="Create New Version"
                content={
                    <p>
                        Are you sure you want to create this new version? 
                        {versionForm.makeCurrent 
                            ? " This will set it as the current active version." 
                            : " This will NOT set it as current; you can activate it later from the Versions tab."}
                    </p>
                }
                confirmLabel="Create Version"
            />
        </>
    );
};
